import { useRef } from "react";
import { Box, IconButton, Chip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useTranslation } from "react-i18next";
import { colorBorder, colorError, colorTextMuted } from "src/design/tokens";
import { GalleryItem } from "../types";
import { enforceImageRatio, fileToDataUrl } from "../utils/imageUtils";

const CELL_RADIUS = "8px";
const MIN_CELLS = 3;

interface Props {
  images: GalleryItem[];
  isEditing: boolean;
  multiple?: boolean;
  onDelete: (item: GalleryItem) => void;
  onAdd: (items: GalleryItem[]) => void;
}

export const ImageGrid = ({
  images,
  isEditing,
  multiple = true,
  onDelete,
  onAdd,
}: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList) => {
    const processed: GalleryItem[] = [];
    for (const file of Array.from(files)) {
      const resized = await enforceImageRatio(file);
      const data_url = await fileToDataUrl(resized);
      processed.push({ file: resized, data_url, isPending: true });
    }
    onAdd(processed);
  };

  // In edit mode we always show at least MIN_CELLS upload slots after the images.
  // In view mode we pad to a full row of 3.
  const paddedCount = Math.max(images.length, MIN_CELLS);
  const emptyCount = paddedCount - images.length;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.length) {
            handleFiles(e.target.files);
            e.target.value = "";
          }
        }}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 1.5,
        }}
      >
        {/* Existing images */}
        {images.map((item, index) => (
          <Box
            key={item.uri ?? item.data_url ?? index}
            sx={{
              position: "relative",
              aspectRatio: "23/20",
              borderRadius: CELL_RADIUS,
              overflow: "hidden",
              border: `1px solid ${colorBorder}`,
            }}
          >
            <img
              src={item.url ?? item.data_url}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />

            {item.isPending && (
              <Box sx={{ position: "absolute", top: 4, left: 4 }}>
                <Chip
                  label={t("gallery.new")}
                  size="small"
                  sx={{
                    fontSize: "0.6rem",
                    height: 18,
                    backgroundColor: "rgba(26,188,156,0.9)",
                    color: "#fff",
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}

            {isEditing && (
              <IconButton
                size="small"
                onClick={() => onDelete(item)}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  backgroundColor: "rgba(255,255,255,0.92)",
                  padding: "3px",
                  "&:hover": { backgroundColor: "#fff", color: colorError },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        ))}

        {/* Empty cells */}
        {Array.from({ length: emptyCount }).map((_, i) =>
          isEditing ? (
            // Upload slot — clicking opens file picker
            <Box
              key={`empty-${i}`}
              onClick={() => inputRef.current?.click()}
              sx={{
                aspectRatio: "23/20",
                borderRadius: CELL_RADIUS,
                border: `2px dashed ${colorBorder}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                cursor: "pointer",
                "&:hover": {
                  borderColor: "#9CA3AF",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <FileUploadOutlinedIcon
                sx={{ fontSize: 24, color: colorTextMuted }}
              />
              <Typography
                variant="caption"
                sx={{ color: colorTextMuted, fontWeight: 500 }}
              >
                {t("gallery.addPhoto")}
              </Typography>
            </Box>
          ) : (
            // Placeholder in view mode
            <Box
              key={`placeholder-${i}`}
              sx={{
                aspectRatio: "23/20",
                borderRadius: CELL_RADIUS,
                backgroundColor: "#F3F4F6",
                border: `1px solid ${colorBorder}`,
              }}
            />
          ),
        )}
      </Box>
    </>
  );
};
